import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/common/ErrorBoundary';
import { AuthProvider } from './context/AuthContext';
import { RentalProvider } from './context/RentalContext';

import './utils/apiClient';
import { USER_ROLES } from './utils/constants';

// Common components
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import ProtectedRoute from './components/common/ProtectedRoute';

// Core pages
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';

// Rental Pages
import RentalSearch from './pages/rental/RentalSearch';
import BikeList from './pages/rental/BikeList';
import BikeDetails from './pages/rental/BikeDetails';
import RentalCheckout from './pages/rental/Checkout';
import MyBookings from './pages/rental/MyBookings';
import DocumentVerification from './pages/rental/DocumentVerification';
import UserInvoice from './pages/rental/invoice/UserInvoice';
import PaymentResult from './pages/rental/PaymentResult';

// Quicklinks
import AboutUs from './components/common/quicklinks/AboutUs';
import ContactUs from './components/common/quicklinks/ContactUs';
import Faq from './components/common/quicklinks/Faq';
import RefundPolicy from './components/common/quicklinks/RefundPolicy';
import ShippingPolicy from './components/common/quicklinks/ShippingPolicy';
import ReturnPolicy from './components/common/quicklinks/ReturnPolicy';
import TermsAndConditions from './components/common/quicklinks/TermsAndConditions';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const NotFoundPage = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#eff6ff,#fef2f2)' }}>
    <div className="text-center max-w-md mx-auto p-8 bg-white rounded-2xl shadow-xl">
      <div className="text-7xl font-black mb-4" style={{ background: 'linear-gradient(135deg,#1e40af,#dc2626)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
        404
      </div>
      <h2 className="text-xl font-bold text-gray-700 mb-3">Page Not Found</h2>
      <p className="text-gray-500 mb-6">The page you're looking for took a detour and got lost in the bike lanes!</p>
      <a
        href="/"
        className="inline-flex items-center justify-center px-6 py-3 rounded-xl text-white font-semibold shadow-lg transition-all hover:scale-105"
        style={{ background: 'linear-gradient(135deg,#1e40af,#dc2626)' }}
      >
        🏍️ Back to Home
      </a>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <RentalProvider>
            <ScrollToTop />
            <div className="min-h-screen bg-gray-50 flex flex-col">
              <Navbar />
              <main className="flex-1">
                <Routes>
                  {/* Main rental search / homepage */}
                  <Route path="/" element={<RentalSearch />} />
                  <Route path="/rental/search" element={<RentalSearch />} />
                  <Route path="/search" element={<RentalSearch />} />

                  {/* Auth */}
                  <Route
                    path="/login"
                    element={
                      <ProtectedRoute requireAuth={false} redirectIfAuthenticated={true}>
                        <Login />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/register"
                    element={
                      <ProtectedRoute requireAuth={false} redirectIfAuthenticated={true}>
                        <Register />
                      </ProtectedRoute>
                    }
                  />

                  {/* Profile */}
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute requireAuth={true}>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />

                  {/* Rental Routes */}
                  <Route path="/bikes" element={<BikeList />} />
                  <Route path="/rental/bikes" element={<BikeList />} />
                  <Route path="/rental/invoice/:bookingId" element={<UserInvoice />} />
                  <Route
                    path="/rental/search/checkout"
                    element={
                      <ProtectedRoute requireAuth={true} redirectTo="/login" showRoleError={false}>
                        <RentalCheckout />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rental/bike/:id"
                    element={
                      <ProtectedRoute requireAuth={true} redirectTo="/login" showRoleError={false}>
                        <BikeDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/bikes/:id"
                    element={
                      <ProtectedRoute requireAuth={true}>
                        <BikeDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rental/search/my-bookings"
                    element={
                      <ProtectedRoute requireAuth={true}>
                        <MyBookings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rental/booking/:id"
                    element={
                      <ProtectedRoute requireAuth={true}>
                        <RentalCheckout />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/booking/:id"
                    element={
                      <ProtectedRoute requireAuth={true}>
                        <RentalCheckout />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/document-verification"
                    element={
                      <ProtectedRoute requireAuth={true}>
                        <DocumentVerification />
                      </ProtectedRoute>
                    }
                  />

                  {/* PayU payment result pages — backend redirects here after
                      verifying payment via /api/payment/payu/{success,failure,cancel} */}
                  <Route
                    path="/payment-success"
                    element={
                      <ProtectedRoute requireAuth={true}>
                        <PaymentResult status="success" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/payment-failure"
                    element={
                      <ProtectedRoute requireAuth={true}>
                        <PaymentResult status="failure" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/payment-cancel"
                    element={
                      <ProtectedRoute requireAuth={true}>
                        <PaymentResult status="cancel" />
                      </ProtectedRoute>
                    }
                  />

                  {/* Quicklinks */}
                  <Route path="/about-us" element={<AboutUs />} />
                  <Route path="/about" element={<AboutUs />} />
                  <Route path="/contact-us" element={<ContactUs />} />
                  <Route path="/contact" element={<ContactUs />} />
                  <Route path="/faq" element={<Faq />} />
                  <Route path="/refund-policy" element={<RefundPolicy />} />
                  <Route path="/refund" element={<RefundPolicy />} />
                  <Route path="/privacy" element={<RefundPolicy />} />
                  <Route path="/shipping-policy" element={<ShippingPolicy />} />
                  <Route path="/return-policy" element={<ReturnPolicy />} />
                  <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                  <Route path="/terms" element={<TermsAndConditions />} />

                  {/* Admin */}
                  <Route
                    path="/admin/*"
                    element={
                      <ProtectedRoute requireAuth={true} requiredRole={USER_ROLES.ADMIN} showRoleError={true}>
                        <div className="min-h-screen flex items-center justify-center bg-gray-50">
                          <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
                            <div className="text-5xl mb-4">👨‍💼</div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
                            <p className="text-gray-500">Admin functionality is under development.</p>
                          </div>
                        </div>
                      </ProtectedRoute>
                    }
                  />

                  {/* 404 */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </main>
              <Footer />
            </div>
          </RentalProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
